import React from 'react';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="section-gap py-12">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-muted hover:text-text transition-colors text-sm"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        Back
      </button>

      <div className="space-y-8 max-w-3xl">
        <div>
          <h1 className="font-display text-5xl tracking-display text-text uppercase">Privacy Policy</h1>
          <p className="font-serif-display italic text-muted text-lg mt-2">Effective date: March 1, 2026</p>
        </div>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">1. Introduction</h2>
          <p className="text-muted leading-relaxed">
            BunyadKar ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and the choices you have. By using our platform, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">2. Information We Collect</h2>
          <p className="text-muted leading-relaxed">We collect the following categories of information:</p>
          <ul className="text-muted leading-relaxed space-y-1 list-disc list-inside">
            <li><strong className="text-text">Account information:</strong> name, email address, password (hashed), and role</li>
            <li><strong className="text-text">Profile information:</strong> bio, company, title, expertise, and profile photo</li>
            <li><strong className="text-text">Usage data:</strong> pages visited, features used, session duration, and click events</li>
            <li><strong className="text-text">Content you create:</strong> startup profiles, milestone submissions, pitch decks, and messages</li>
            <li><strong className="text-text">Technical data:</strong> IP address, browser type, device identifiers, and cookies</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">3. How We Use Your Information</h2>
          <p className="text-muted leading-relaxed">We use your information to:</p>
          <ul className="text-muted leading-relaxed space-y-1 list-disc list-inside">
            <li>Provide, operate, and improve the BunyadKar platform</li>
            <li>Personalise your experience and match you with relevant mentors and programmes</li>
            <li>Send transactional emails such as account verification and session reminders</li>
            <li>Send optional marketing communications (you may opt out at any time)</li>
            <li>Detect and prevent fraud, abuse, and security incidents</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">4. Information Sharing</h2>
          <p className="text-muted leading-relaxed">
            We do not sell your personal data. We may share your information with:
          </p>
          <ul className="text-muted leading-relaxed space-y-1 list-disc list-inside">
            <li><strong className="text-text">Service providers:</strong> third-party vendors who help us operate the platform (e.g., cloud hosting, authentication, analytics) under strict confidentiality agreements</li>
            <li><strong className="text-text">Mentors and administrators:</strong> limited profile information is visible to other platform participants as necessary to deliver our services</li>
            <li><strong className="text-text">Legal authorities:</strong> when required by law or to protect the rights and safety of our users</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">5. Data Retention</h2>
          <p className="text-muted leading-relaxed">
            We retain your personal data for as long as your account is active or as needed to provide the Service. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law or for legitimate business purposes such as resolving disputes.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">6. Security</h2>
          <p className="text-muted leading-relaxed">
            We implement industry-standard technical and organisational measures to protect your data, including encrypted transmission (TLS), hashed password storage, and access controls. No method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">7. Your Rights</h2>
          <p className="text-muted leading-relaxed">
            Depending on your jurisdiction, you may have the right to access, correct, delete, or export your personal data. You can update most profile information directly in Settings. To exercise other rights or to submit a data deletion request, contact us at{' '}
            <a href="mailto:privacy@founderslab.io" className="text-chart hover:text-chart/80">
              privacy@founderslab.io
            </a>
            .
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">8. Cookies</h2>
          <p className="text-muted leading-relaxed">
            We use cookies and similar tracking technologies to maintain your session, remember your preferences, and analyse platform usage. You can control cookie settings through your browser, though disabling certain cookies may affect platform functionality.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl tracking-display-sm text-text uppercase">9. Contact</h2>
          <p className="text-muted leading-relaxed">
            If you have questions or concerns about this Privacy Policy, please contact our privacy team at{' '}
            <a href="mailto:privacy@founderslab.io" className="text-chart hover:text-chart/80">
              privacy@founderslab.io
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
